import Worker from '../../../worker';
import exifr from 'exifr';
import ExifReader from 'exifreader';
import Compressor from 'compressorjs';
import { ITrimEditorManagerBase } from '../trim-editor.manager';
import { TrimUiManager } from '../ui/trim.ui.manager';
import { TrimEditableImageManager } from '../editable-image/trim.editable-image.manager';
import EventEmitter from 'eventemitter3';
import { TrimProcess } from './trim.process';
import { TrimUploadRequest } from '../upload/trim.upload-request';
import { TrimUploadManager } from '../upload/trim.upload.manager';
import { ApiImagesGet, ImagesGetResponse } from '../../../api/front/images/api-images';
import { ResponseBase } from '../../../api/response-base';
import { getOrientation, Orientation } from 'get-orientation/browser';
import { TrimConnectCloudFolderRequest } from '../upload/trim.connect-cloud-folder-request';
import { RecordData } from '../../../utilities/utility.type';
import { IMAGES_KIND } from '../../../layout-editor/manager/image-processor/image-processor.manager';
import { cloneDeep } from 'lodash';
import { TrimEditableImage } from '../image-edit/trim.editable-image';
import { store } from '../../../app/store';
import { pushExifColorSpaceWarningDialog } from '../../../utilities/exif-color-space-warning';

type EventType = {
  'change-queue': (e: {}) => void,
};

export type PrintProcessList = {
  queue: TrimProcess[],
  complete: TrimProcess[],
}
export type PrintUploadProgressList = PrintProcessList & {
  error: TrimProcess[],
}

export class TrimImageProcessorManager extends EventEmitter<EventType> implements ITrimEditorManagerBase {

  private static _ins: TrimImageProcessorManager;
  private initialized = false;
  // private processQueue: TrimProcess[] = [];
  private processQueue = new Map<string, PrintProcessList>();
  private uploadProgress = new Map<string, PrintUploadProgressList>();
  private uiManager!: TrimUiManager;
  private editableImageManager!: TrimEditableImageManager;
  private uploadManager!: TrimUploadManager;
  private isBusy = false;

  private constructor() {
    super();
  }

  static get ins() {
    if (TrimImageProcessorManager._ins) {
      return TrimImageProcessorManager._ins;
    }
    TrimImageProcessorManager._ins = new TrimImageProcessorManager();
    return TrimImageProcessorManager._ins;
  }

  destroy(): void {
    // this.processQueue = [];
    this.processQueue.clear();
    this.isBusy = false;
  }

  di(): void {
  }

  initialize() {
    if (this.initialized) {
      // console.error('すでに初期化済みです !!');
      return;
    }
    this.editableImageManager = TrimEditableImageManager.ins;
    this.uiManager = TrimUiManager.ins;
    this.uploadManager = TrimUploadManager.ins;
    this.initialized = true;
    this.processQueue.clear();
    this.isBusy = false;
    this.uploadProgress.clear();
    this.addEvent();
  }

  enqueue(...processes: TrimProcess[]) {
    processes.forEach((process) => {
      const target = this.processQueue.get(process.shopOrderId);
      const progressTarget = this.uploadProgress.get(process.shopOrderId);
      if(!target) {
        this.processQueue.set(process.shopOrderId, { queue: [process], complete: [] });
      } else {
        target.queue.push(process);
      }
      if (!process.restore) {
        TrimUiManager.ins.emit('r->t:get-current-queue-count', this.uploadProgress);
        if(!progressTarget) {
          this.uploadProgress.set(process.shopOrderId, { queue: [process], complete: [], error: [] });
        } else {
          progressTarget.queue.push(process);
        }
      }
    });
    // this.processQueue.push(...processes);
    // // NOTE: kindが6の場合のみ、ユーザーが手動で消した可能性があるため復元をViewには反映しないようにする
    // const isPicture = processes.find(v => v.kind === '6');
    // if (isPicture) {
    //   this.processCounter += processes.filter(v => !v.isDelete).length;
    //   this.processQueue.sort((a, b) => a.isDelete === b.isDelete ? 0 : a.isDelete ? 1 : -1);
    // }
    this.emit('change-queue', {});
  }

  dequeue(process?: TrimProcess) {
    if (process) {
      const target = this.processQueue.get(process.shopOrderId);
      if (target) {
        const index = target.queue.findIndex(v => process === v);
        if (index > -1) {
          const [completeProcess] = target.queue.splice(index, 1);
          if (completeProcess) target.complete.push(completeProcess);
        }
        if (!target.queue.length) {
          this.processQueue.delete(process.shopOrderId);
        }
      }
    }
    this.emit('change-queue', {});
  }

  async do(process: TrimProcess) {
    let editableImage = await process.all();
    /* 通常画像アップロード */
    editableImage.isDelete = !!process.isDelete;
    process.onCreatedEditableImage(editableImage);
    TrimUiManager.ins.emit('t->r:on-create-editable-image', {
      editableImage,
    });
    this.editableImageManager.push('list', editableImage);
    const editableImageId = editableImage.id;
    if (!editableImage.flags.uploaded) {
      // - アップロード -
      this.editableImageManager.toUploading(editableImageId);
      if (editableImage.original) {
        const uploadRequest = new TrimUploadRequest(
          editableImage,
          editableImage.original,
          () => {
            this.editableImageManager.toUploading(editableImageId);
          },
          (path) => {
            this.editableImageManager.toUploading(editableImageId, false);
            this.editableImageManager.toUploaded(editableImageId, path);
            editableImage.path = path;
            this.uploaded(process, editableImage);
            process.onUploaded(editableImage);
          },
          () => {
            this.editableImageManager.toUploading(editableImageId, false);
            this.editableImageManager.toUploaded(editableImageId, '', false);
            this.uploaded(process, editableImage, true);
            process.onError?.();
          },
        );
        this.uploadManager.enqueue(uploadRequest);
        // process.onCreatedEditableImage(editableImage);
      } else {
        this.editableImageManager.toUploading(editableImageId, false);
        this.editableImageManager.toUploaded(editableImageId, editableImage.path);
        process.onUploaded(editableImage);
      }
    } else {
      this.editableImageManager.toUploaded(editableImageId, editableImage.path);
    }
  }

  private addEvent() {
    // 画像アップロードは各種でイベント作成
    this.uiManager.on('r->t:trim-add-image', async (e) => {
      // console.log('r->t:add-image : ', e);
      // sRGB以外の色空間警告はバッチにつき1回のみ表示（通常注文の仕様に合わせる）
      let exifWarned = false;
      // process を作る
      const processList = e.files.map((file, i) => new TrimProcess(
        file,
        e.kijshopCd,
        e.shopOrderId,
        '',
        IMAGES_KIND.PRINT,
        e.onUploaded ? ((p) => e.onUploaded?.({ editableImage: p, index: i })) : undefined,
        (p, sameImage) => {
          const stopWarning = store.getState().storage.isStopDialog.exifWarning;
          if (!exifWarned && !stopWarning && String(p.exif.colorSpace) !== '1') {
            exifWarned = true;
            pushExifColorSpaceWarningDialog(store.dispatch, p.exif.colorSpace);
          }
          e.onCreatedEditableImage?.({
            editableImage: p,
            index: i,
            sameImage,
          });
        },
        null,
      ));
      // enqueue する
      this.enqueue(...processList);
    });
    this.uiManager.on('r->t:trim-add-image:restore', async (e) => {
      // const hashList: { path: string, hash: string, orientation?: Orientation }[] = existImageList
      //   .map((v) => ({ path: v.filename, hash: v.hash || '', orientation: v.exifOrientation }))
      //   .filter((v) => v.hash);
      const findQueue = Array.from(this.processQueue.values()).map((v) => v.queue).flat().find((v) => v.imgId === e.data.id);
      const editableImage = TrimEditableImageManager.ins.list.find((v) => v.id === e.data.id && v.shopOrderId === e.shopOrderId && v.kijshopCd === e.kijshopCd);

      if (
        editableImage
      ) {
        console.warn('DL済 !!');
        this.editableImageManager.change();
        e.onSuccess(editableImage)
        return;
      }
      if (findQueue) {
        console.warn('Queue登録済');
        findQueue.onCreatedEditableImage = e.onSuccess;
        return;
      }
      const process = new TrimProcess(
        null,
        e.kijshopCd,
        e.shopOrderId,
        null,
        IMAGES_KIND.PRINT,
        undefined,
        e.onSuccess,
        {
          originalName: e.data.orgFilename,
          name: e.data.filename,
          exif: {
            colorSpace: e.data.exifColorSpace,
            model: e.data.exifModel,
            orientation: e.data.exifOrientation,
            make: e.data.exifMake,
            createDate: e.data.exifCreated,
          },
          width: Number(e.data.width) || 0,
          height: Number(e.data.height) || 0,
          thumbnailUrl: e.data.pathThumb,
          hash: e.data.hash || '',
        },
        true,
        e.data.path,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        e.data.id,
      );
      console.group('===== debug =====');
      console.log('processList : ', process);
      console.groupEnd();
      this.enqueue(process);
    });
    // PNG画像アップロード
    this.uiManager.on('r->t:add-image-png', (e) => {
      const processList = e.files.map((file, i) => new TrimProcess(
        file,
        e.kijshopCd,
        e.shopOrderId,
        e.orderId,
        '5',
        e.onUploaded ? ((p) => e.onUploaded?.({ editableImage: p, index: i })) : undefined,
        e.onCreatedEditableImage ? ((p) => e.onCreatedEditableImage?.({ editableImage: p, index: i })) : undefined,
        null,
        e.restore,
        e.path,
      ));
      this.enqueue(...processList);
    });
    // レイアウトサムネイルアップロード
    this.uiManager.on('r->t:add-layout-comp', (e) => {
      // process を作る
      const process = new TrimProcess(
        e.file,
        e.kijshopCd,
        e.shopOrderId,
        e.orderId,
        '2',
        e.callback ? ((p) => e.callback?.({ editableImage: p })) : undefined,
        undefined,
        null,
        e.restore,
        e.path,
        '',
        false,
        undefined,
        undefined,
        undefined,
        e.onError,
      );
      // enqueue する
      this.enqueue(process);
    });
    // PNG画像アップロード
    this.uiManager.on('r->t:add-image:direct', (e) => {
      const process = new TrimProcess(
        e.file,
        e.kijshopCd,
        e.shopOrderId,
        e.orderId,
        '5',
        (p) => e.callback({ editableImage: p }),
        (p) => e.onCreatedEditableImage?.({ editableImage: p }),
        null,
        e.restore,
        e.path,
      );
      // enqueue する
      this.enqueue(process);
    });

    this.on('change-queue', () => {
      this.onChangeQueue();
    });
  }

  private async onChangeQueue() {
    const currentShopOrder = await this.getCurrentShopOrderId();
    const shopOrderCurrent = this.processQueue.get(currentShopOrder ?? '');
    const margeList = Array.from(this.processQueue.values());
    const targetList = shopOrderCurrent?.queue?.length
      ? (shopOrderCurrent?.queue ?? [])
      : Array.from(margeList)[0]?.queue ?? [];
    this.uiManager.emit('t->r:get-shop-order-id', () => {});
    if (!margeList.length) {
      console.log('実行する TrimProcess がありません。: ', this.processQueue);
      return;
    }
    if (this.isBusy) {
      return;
    }
    this.isBusy = true;
    const startTime = window.performance.now();
    const firstProcess = targetList[0];
    console.group(`[img-prc][${firstProcess.shopOrderId}] (${margeList.length}) process start ...`);
    this.do(firstProcess)
      .then(() => {
        this.isBusy = false;
        const totalTime = window.performance.now() - startTime;
        console.log(`[imp-prc] finish (${totalTime} ms)`);
        console.groupEnd();
        this.dequeue(firstProcess);
      });
  }

  private uploaded(process: TrimProcess, editableImage: TrimEditableImage, isError?: boolean) {
    const target = this.uploadProgress.get(process.shopOrderId);
    let lastUpload = false;
    if (process.restore) return;
    if (target) {
      const index = target.queue.findIndex(v => process === v);
      if (index > -1) {
        const [completeProcess] = target.queue.splice(index, 1);
        if (completeProcess) {
          if (!isError) {
            TrimUiManager.ins.emit('t->r:uploaded-editable-image', { editableImage })
            target.complete.push(completeProcess);
          } else {
            target.error.push(completeProcess);
          };
        }
      }
      if (!target.queue.length) {
        lastUpload = true;
        const clone = cloneDeep(this.uploadProgress);
        if (!process.restore) {
          TrimUiManager.ins.emit('r->t:get-current-queue-count', clone);
        }
        this.uploadProgress.delete(process.shopOrderId);
      }
    }
    if (!process.restore) {
      if (lastUpload) {
        window.setTimeout(() => {
          TrimUiManager.ins.emit('r->t:get-current-queue-count', this.uploadProgress);
        }, 2000);
      } else {
        TrimUiManager.ins.emit('r->t:get-current-queue-count', this.uploadProgress);
      }
    }
  }

  private getCurrentShopOrderId() {
    return new Promise<string | undefined>((resolve) => {
      this.uiManager.emit('t->r:get-shop-order-id', (shopOrderId) => resolve(shopOrderId));
    });
  }
  private static worker = new Worker();

  static resize(file: File) {
    const size = 1000;
    return new Promise<File>((resolve, reject) => {
      setTimeout(() => {
        console.log('file: ', file);
        new Compressor(
          file,
          {
            maxWidth: size,
            maxHeight: size,
            success(blob: Blob) {
              console.log('success');
              resolve(new File([blob], file.name, { type: file.type }));
            },
          },
        );
      });
    });
  }

  static fileToBase64(file: File): Promise<string> {
    return this.worker.fileToBase64(file);
  }

  static base64ToFile(base64: string, name: string, type: string): Promise<File> {
    return this.worker.base64ToFile(base64, name, type);
  }

  static loadExif(file: File) {
    return exifr.parse(file)
      .then(async (v) => {
        let orientation!: Orientation;
        try {
          orientation = await getOrientation(file);
        } catch (error) {
          orientation = NaN;
        }
        const exif = v ? (
          {
            createDate: v.CreateDate || v.ModifyDate || '',
            colorSpace: v.ColorSpace,
            make: v.Make || '',
            model: v.Model || '',
            width: v.ExifImageWidth || 0,
            height: v.ExifImageHeight || 0,
            orientation,
          }
        ) : (
          {
            createDate: '',
            colorSpace: '',
            make: '',
            model: '',
            width: 0,
            height: 0,
            orientation: 0,
          }
        );
        /* TODO 2024/3/26
         * 元々使用していたライブラリ(exifr)だと、
         * ColorSpaceに"1"のように文字列で入っていた場合検知ができないため
         * exifrで取得した情報が存在し、colorSpaceの情報がない時のみ、別のライブラリを使用してColorSpaceを取得
         * 文字列でそのまま入ってくるためnumber型にキャストして再代入する
         * 最終的には使用ライブラリを丸ごと移行したい
        */
        if (v && !v.colorSpace) {
          const colorSpace = (await ExifReader.load(file)).ColorSpace;
          if (colorSpace?.value && !isNaN(Number(colorSpace.value))) {
            exif.colorSpace = Number(colorSpace.value);
          }
        }
        return exif;
      });
  }

  static loadExifOrigin(file: File) {
    return exifr.parse(file);
  }

  static getImageSize(file: File): Promise<{ w: number, h: number }> {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.src = url;
    return new Promise((resolve) => {
      img.onload = () => {
        const w = img.width;
        const h = img.height;
        URL.revokeObjectURL(url);
        resolve({ w, h });
      };
    });
  }
}

TrimImageProcessorManager.ins.initialize();
